/* eslint-disable no-use-before-define */
import React from 'react';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import Head from 'next/head';
import directory from '@tutorbot/directory-api';
import { Modal, Skeleton } from 'antd';
import { withRouter, useRouter } from 'next/router';
// import { StripeService } from 'stripe';
import get from 'lodash/get';
import { loadStripe } from '@stripe/stripe-js';
import Button from '../../components/Button';
import { LocalizationContext } from '../../hoc/Localization';
import { LocationContext } from '../../hoc/Location';
import { RouteContext } from '../../hoc/Route';
import { UserContext } from '../../hoc/User';
import { SecurityContext } from '../../hoc/Security';
import { SettingsContext } from '../../hoc/Settings';
import { getCourseByShortId, toggleFavourite } from '../../utils/directory';
import api, { orderCourseByShortIdAndPaymentMethod, getPaths } from '../../utils/api';
import { courseVisited } from '../../utils/events/courses';
import { cleanString } from '../../utils';
import Header from '../../components/CourseComponents/HeaderShow';
import Container from '../../components/Home/Container';
import useOnScreen from '../../components/useOnScreen';
import Router from '../../routes';
import CardListHeader from '../../components/CardList/Header';
import CardList from '../../components/CardList';
import styles from '../course/styles.less';
import { PaymentMethodsEnum } from '../../components/PaymentProcess/payment-methods-enum';
import SubscribeCourses from '../../components/Home/SubscribeCourses';
import RegisterList from '../../components/CourseComponents/RegisterList';
import ModalCoursesSubscribe from '../../components/Home/ModalCoursesSubscribe';
import bugsnagClient from '../../utils/bugsnag';
import ShareModal from '../../components/TinyComponents/ShareModal';

const StripeService = {
    redirectToPayment: async order => {
        const stripe = await loadStripe(order.publishableKey);
        const { error } = await stripe.redirectToCheckout({
            sessionId: get(order, 'session.id'),
        });

        if (error) {
            /* eslint-disable */
            throw new Error(`Strapi redirectToCheckout failed ERROR: ${error && error.message}`); // eslint-disable-line no-use-before-define
            /* eslint-enable */
        }
    },
};

const IdramService = {
    redirectToPayment: async order => {
        const formSubmitUrl = get(order, 'formSubmitUrl');
        const formFields = get(order, 'formFields') || {};

        if (!formSubmitUrl || !formFields) {
            throw new Error(
                'Failed to process Idram payment submit form as server did not return required "formSubmitUrl" and "formFields" data',
            );
        }

        // @TODO: review this and if possible have nicer solution, for now this seems ok
        // create form dynamically and auto-submit
        const form = document.createElement('form');
        form.setAttribute('method', 'post');
        form.setAttribute('action', formSubmitUrl);
        form.style.display = 'none';

        Object.entries(formFields).forEach(([name, value]) => {
            const input = document.createElement('input');

            input.setAttribute('type', 'hidden');
            input.setAttribute('name', name);
            input.setAttribute('value', value);

            form.appendChild(input);
        });

        const submit = document.createElement('input');
        submit.setAttribute('type', 'submit');
        submit.setAttribute('value', 'Submit');
        form.appendChild(submit);

        document.getElementsByTagName('body')[0].appendChild(form);

        form.submit();
    },
};

const TitleContent = dynamic(() => import('../../components/TinyComponents/TitleContent'));
const Info = dynamic(() => import('../../components/CourseComponents/InfoShow'));
const Register = dynamic(() => import('../../components/CourseComponents/Register'));
const Loading = dynamic(() => import('../../components/Loading'));
const JsonLD = dynamic(() => import('../../components/JsonLD/Course'));
const ReviewForm = dynamic(() => import('../../components/Reviews/ReviewForm'));
const Schedule = dynamic(() => import('../../components/CourseComponents/ScheduleShow'), {
    loading: () => <Skeleton active round paragraph={{ rows: 2 }} />,
});
const SchoolInfo = dynamic(() => import('../../components/SchoolInfo'));
const Blog = dynamic(() => import('../../components/SchoolComponents/Blog'));
const Reviews = dynamic(() => import('../../components/Reviews'));
const CardListJob = dynamic(() => import('../../components/CardListJob'));
const Professions = dynamic(() => import('../../components/Paths/pathNew/Professions'));

const Map = dynamic(() => import('../../components/CustomMap'), {
    loading: () => (
        <Skeleton.Button
            active
            round
            paragraph={{ rows: 2 }}
            style={{ height: '300px', width: '100%' }}
        />
    ),
});
const ErrorComponent = dynamic(() => import('../_error'));
const VideoPlayer = dynamic(() => import('../../components/VideoPlayer'));

const Course = ({ datas, load, router }) => {
    const { locale, t, translate } = React.useContext(LocalizationContext);
    const { locations } = React.useContext(LocationContext);

    const {
        params: { id, register: reg },
    } = React.useContext(RouteContext);
    const { user, refreshUser } = React.useContext(UserContext);
    const { register, setRegister, setFavourite } = React.useContext(SecurityContext);
    const {
        domainSettings: { id: portalId, domain, showJobs },
    } = React.useContext(SettingsContext);

    const [selectedSchedule, setSelectedSchedule] = React.useState({});
    const [orderLoading, setOrderLoading] = React.useState(false);
    const [shareUrl, setShareUrl] = React.useState(null);
    const [reviews, setReviews] = React.useState([]);
    const [blog, setBlog] = React.useState({});
    const [courseRaw, setCourse] = React.useState(datas.course || {});
    const [relatedCourses, setRelatedCourses] = React.useState(datas.relatedCourses || []);
    const [otherCourses, setOtherCourses] = React.useState(datas.courses || []);
    const [loading, setLoading] = React.useState(load);
    const [key, setKey] = React.useState(Date.now());
    const [mapShow, setMapShow] = React.useState();
    const [desktop, setDesktop] = React.useState();
    const [videoLoading, setVideoLoading] = React.useState(true);
    const [smallScreen, setSmallScreen] = React.useState(true);
    const [fetched, setFetched] = React.useState(false);
    const [requests, setRequests] = React.useState(datas.requests);
    const [canRegister, setCanRegister] = React.useState(true);
    const [payedScheduleIds, setPayedScheduleIds] = React.useState(datas.payedScheduleIds || []);
    const [isRegistered, setIsRegistered] = React.useState(false);
    const [lastRequestId, setLastRequestId] = React.useState(datas.lastRequestId);
    const [join, setJoin] = React.useState(false);
    const [isFavourite, setIsFavourite] = React.useState(false);
    const [justRequested, setJustRequested] = React.useState([]);
    const [loadingFavourite, setLoadingFavourite] = React.useState(false);
    const [favourites, setFavourites] = React.useState(
        datas.course && datas.course.favouriteNumber,
    );
    const [shareModalOpen, setShareModalOpen] = React.useState(false);
    const [paths, setPaths] = React.useState([]);
    const [jobsData, setJobsData] = React.useState();
    const [modalShow, setModalShow] = React.useState();

    const data = datas.course;

    const routeParams = useRouter();
    const { reference, payment } = routeParams.query;

    const firstRef = React.useRef();
    const secondRef = React.useRef();
    const thirdRef = React.useRef();
    const countries = locations
        .filter(location => location.default)
        .map(location => location.country);

    React.useEffect(() => {
        if (reg === 'register' && canRegister) {
            setRegister(true);
        } else {
            setRegister(false);
        }
    }, [canRegister, reg]);

    React.useEffect(() => {
        setJustRequested([]);
    }, [id]);

    React.useEffect(() => {
        const windowWidth = window.screen.width;
        if (windowWidth > 768) {
            setDesktop(true);
        }

        if (windowWidth > 450) {
            setSmallScreen(false);
        }
        setShareUrl(window.location.href);

        if (!load || (data && data.shortId === id)) {
            if (data && data.schedules && data.schedules.length) {
                const index = data.schedules.length - 1;
                setSelectedSchedule(data.schedules[index]);
            }

            courseVisited({ ...data, portalId });

            setKey(Date.now());
            return;
        }

        setLoading(true);

        if (courseRaw.shortId !== id) {
            getCourseByShortId(id, {
                countries,
            }).then(res => {
                if (res) {
                    const {
                        course,
                        relatedCourses: fetchedRelatedCourses,
                        courses: fetchedOtherCourses,
                        requests: fetchedRequests,
                        payedScheduleIds: fetchedPayedScheduleIds,
                        lastRequestId: fetchedLastRequestId,
                    } = res;
                    setCourse(course);
                    setRequests(fetchedRequests);
                    setRelatedCourses(fetchedRelatedCourses);
                    setOtherCourses(fetchedOtherCourses);
                    setPayedScheduleIds(fetchedPayedScheduleIds);
                    setLastRequestId(fetchedLastRequestId);
                    if (course) {
                        const index = course.schedules.length - 1;
                        setSelectedSchedule(course.schedules[index]);
                        setFavourites(course.favouriteNumber);
                    }
                    setLoading(false);
                    courseVisited(course);
                    setKey(Date.now());
                }
            });
        } else {
            setLoading(false);
        }
    }, [id]);

    React.useEffect(() => {
        if (courseRaw && courseRaw.id && courseRaw.school && courseRaw.school.blogShortId) {
            (async () => {
                setBlog(
                    (await directory().blog.where('shortId', courseRaw.school.blogShortId).get())
                        .data[0],
                );
            })();
        }
    }, [courseRaw && courseRaw.school && courseRaw.school.external_id]);

    const containerRef = React.useRef();

    React.useEffect(() => {
        if (router.query.scroll) {
            const reviewPosition = containerRef.current.getBoundingClientRect();
            setTimeout(() => {
                window.scrollTo(400, reviewPosition.top);
            }, 2000);
        }
    }, []);

    const getCompetences = () => {
        let compentences = (courseRaw.skills && courseRaw.skills.map(skill => skill.title)) || [];

        if (
            !compentences[0] &&
            courseRaw.language &&
            !['Armenian', 'German', 'Russina'].includes(courseRaw.language.Name)
        ) {
            compentences = [courseRaw.language.Name];
        }

        if (!compentences[0] && courseRaw.coursetypes && courseRaw.coursetypes.title) {
            compentences = [courseRaw.coursetypes.title];
        }

        return compentences;
    };

    React.useEffect(() => {
        if (courseRaw && courseRaw.language) {
            (async () => {
                const fetchedPaths = await getPaths(domain, getCompetences());
                setPaths(fetchedPaths.data);
            })();
        }
        if (courseRaw && (courseRaw.language || courseRaw.coursetypes)) {
            (async () => {
                setJobsData(
                    (
                        await api.post('/pathJobs', {
                            page: 1,
                            keyWords: [
                                courseRaw.language
                                    ? courseRaw.language.Name
                                    : courseRaw.coursetypes.title,
                            ],
                        })
                    ).data,
                );
            })();
        }
    }, [courseRaw]);

    const isPayed =
        !!payedScheduleIds.find(scheduleId => scheduleId === selectedSchedule.id) ||
        ((!selectedSchedule || !selectedSchedule.start_date) &&
            payedScheduleIds.find(scheduleId => typeof scheduleId === 'undefined'));

    let isOnlinePaymentEnable = false;
    if (courseRaw && courseRaw.school) {
        isOnlinePaymentEnable = courseRaw.school.isOnlinePaymentEnable;
    }

    function shouldDisable(matches) {
        if (isPayed) {
            return true;
        }
        if (
            matches.length > 0 &&
            (!isOnlinePaymentEnable || (isOnlinePaymentEnable && !courseRaw.price))
        ) {
            return true;
        }
        return false;
    }

    React.useEffect(() => {
        if (
            user &&
            courseRaw &&
            (!courseRaw.trainingType ||
                (courseRaw.trainingType && courseRaw.trainingType.title !== 'ELearning'))
        ) {
            const matches = [];
            (requests || []).forEach(req => {
                if (
                    req.course.selectedSchedule &&
                    req.course.selectedSchedule.id === selectedSchedule.id
                ) {
                    matches.push(req);
                }
            });

            if (shouldDisable(matches) && canRegister) {
                setCanRegister(false);
            }

            if (!shouldDisable(matches) && !canRegister) {
                setCanRegister(true);
            }

            if (matches.length > 0 && !isRegistered && !isPayed && isOnlinePaymentEnable) {
                setIsRegistered(true);
            }

            if (isRegistered && (matches.length === 0 || isPayed || !isOnlinePaymentEnable)) {
                setIsRegistered(false);
            }
        }

        if (
            user &&
            courseRaw &&
            courseRaw.trainingType &&
            courseRaw.trainingType.title === 'ELearning' &&
            requests &&
            requests[0] &&
            !join
        ) {
            setJoin(true);
        }
        if (!user) {
            setIsRegistered(false);
            setCanRegister(true);
        }
    }, [requests, selectedSchedule, user, id]);

    React.useEffect(() => {
        if (user && user.favourites) {
            const favourite = user.favourites.find(f => f.course_id === courseRaw.shortId);
            if (favourite && !isFavourite) {
                setIsFavourite(true);
            } else {
                setIsFavourite(false);
            }
        }
    }, [user, courseRaw, id]);

    const showMapFirst = useOnScreen(firstRef, '150px');
    const showMapSecond = useOnScreen(secondRef, '150px');
    const showSubscribeModal = useOnScreen(thirdRef, '150px');
    if (!mapShow && (showMapFirst || showMapSecond)) {
        setMapShow(true);
    }

    if (!modalShow && showSubscribeModal) {
        setModalShow(true);
    }

    const canEdit =
        courseRaw &&
        user &&
        (user.isAdmin ||
            (user.schools &&
                user.schools.length &&
                !!user.schools.find(s => courseRaw.school && s.id === courseRaw.school.id)));

    if (loading) {
        return <Loading full />;
    }

    if (!courseRaw || (courseRaw && !courseRaw.shortId)) {
        return <ErrorComponent />;
    }

    const orderCourse = async (paymentMethod, paymentSubtype) => {
        setOrderLoading(`${paymentMethod}-${paymentSubtype}`);
        try {
            const order = await orderCourseByShortIdAndPaymentMethod(
                courseRaw.shortId,
                paymentMethod,
                locale,
                selectedSchedule && selectedSchedule.id,
                payment,
            );

            switch (paymentMethod) {
                case PaymentMethodsEnum.ARCA:
                    if (order.redirect) {
                        window.location = order.redirect;
                    }
                    break;
                case PaymentMethodsEnum.STRIPE:
                    await StripeService.redirectToPayment(order);
                    break;

                case PaymentMethodsEnum.IDRAM:
                    await IdramService.redirectToPayment(order);
                    break;
                default:
                    throw new Error('Non Supported payment method for order checkout');
            }
        } catch (e) {
            setOrderLoading(false);

            console.error(e);
            bugsnagClient.notify(e);
        }
    };

    const course = {
        school: {},
        category: {},
        language: {},
        location: {},
        ...courseRaw,
        title: courseRaw.title,
        description: translate(courseRaw, 'description'),
        content: translate(courseRaw, 'content'),
        learn: translate(courseRaw, 'learn'),
    };

    const {
        title,
        learn,
        school,
        skills,
        perks,
        content,
        location,
        schedules,
        description,
        category = {},
        videoUrl,
    } = courseRaw;

    const filteredSchedules = schedules
        ? schedules.filter(schedule => {
              const today = new Date();
              const todayString = today.toISOString();
              if (schedule) {
                  if (schedule.end_date) {
                      return schedule.end_date > todayString;
                  }

                  if (schedule.start_date) {
                      return schedule.start_date > todayString;
                  }
              }
              return false;
          })
        : [];

    const metaDescription = cleanString(courseRaw.description);

    const { image } = course;

    const urlFront = image ? image.substring(0, image.lastIndexOf('.')) : '';
    const ext = image ? image.slice(image.lastIndexOf('.'), image.length) : '';
    let mediumUrl = '';
    if (image) {
        if (smallScreen) {
            mediumUrl = urlFront.concat('-small').concat(ext);
        } else {
            mediumUrl = urlFront.concat('-medium').concat(ext);
        }
    }

    const pushReview = review => {
        setReviews([...reviews, review]);
    };

    const handleRegisterClose = () => {
        const params = {
            course: courseRaw.link,
            school: courseRaw.school.link,
            id: courseRaw.shortId,
            locale,
        };
        if (reference) {
            params.queryParams = { reference };
        }
        if (payment) {
            params.queryParams = { ...params.queryParams, payment };
        }
        Router.pushRoute('courseShow', params);
    };

    if (mapShow && !fetched) {
        (async () => {
            if (course) {
                const res = await api.get(`user-ratings/courseReviews/${courseRaw.shortId}`);
                setReviews(res.reviews);
            }
        })();
        setFetched(true);
    }

    async function handleFavouriteClick() {
        if (!user) {
            setFavourite(true);
            return;
        }
        if (user && !loadingFavourite) {
            setLoadingFavourite(true);
            await toggleFavourite(course.shortId, isFavourite, 'course');
            await refreshUser();
            if (isFavourite) {
                setFavourites(Number(favourites) - 1);
            } else {
                setFavourites((Number(favourites) || 0) + 1);
            }
            setLoadingFavourite(false);
        }
        if (!user) {
            setFavourites(true);
        }
    }

    let favouriteUrl = '/static/images/course/heartBlack.svg';
    if (isFavourite) {
        favouriteUrl = '/static/images/course/heartChecked.svg';
    }

    const handleShareClicked = () => {
        setShareModalOpen(!shareModalOpen);
    };

    return (
        <div className={styles.wrapper} key={key}>
            <Head>
                <title>
                    {`${title || ''} | ${(category && translate(category, 'title')) || ''} | ${t(
                        'meta.title.suffix',
                    )}`}
                </title>
                <meta name="description" content={metaDescription} />
                <meta name="Description" content={metaDescription} />
                <meta property="og:type" content="article" />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={metaDescription} />
                <meta property="og:image" content={course.image} />
                <meta property="og:image:width" content="600" />
                <meta property="og:image:height" content="450" />
                <meta property="og:locale" content={locale} />
            </Head>

            {canEdit && (
                <div className="editButtonsContainer">
                    <Button
                        onClick={handleShareClicked}
                        size="default"
                        type="primary"
                        className="editShareButton"
                    >
                        <img src="/static/images/share.svg" alt="share" />
                        <span>{t('school.edit.toolbar.share')}</span>
                    </Button>
                    <ShareModal
                        onCancel={handleShareClicked}
                        visible={shareModalOpen}
                        course={courseRaw}
                    />
                    <Button
                        size="default"
                        type="secondary"
                        data_gh="course_edit_button"
                        className="editButton editModeButton toolBarButton"
                        onClick={() => {
                            Router.pushRoute('course', {
                                course: courseRaw.link,
                                school: courseRaw.school.link,
                                id: courseRaw.shortId,
                                locale,
                            });
                        }}
                    >
                        <img src="/static/images/school/edit.svg" alt="edit" />
                        <span>{t('course.edit.title')}</span>
                    </Button>
                </div>
            )}

            <div className={styles.cover}>
                <img src="/static/images/frontpage/section-illustrations.png" alt="background" />
                <img src="/static/images/frontpage/section-illustrations.png" alt="background" />
                <img
                    className={styles.image}
                    src={!image.includes('static') && !image.includes('fbcdn') ? mediumUrl : image}
                    alt={title}
                />
            </div>
            <Header
                course={course}
                canEdit={canEdit}
                shareUrl={shareUrl}
                schedules={filteredSchedules}
                setRegister={setRegister}
                selectedSchedule={selectedSchedule}
                setSelectedSchedule={setSelectedSchedule}
                mediumUrl={!mediumUrl.includes('fbcdn') ? mediumUrl : image}
                user={user}
                canRegister={canRegister}
                join={join}
                lastRequestId={lastRequestId || ''}
                isFavourite={isFavourite}
                handleFavouriteClick={handleFavouriteClick}
                favourites={favourites}
            />
            <Container>
                <Info
                    description={description}
                    content={content}
                    learn={learn}
                    skills={skills}
                    perks={perks}
                    course={course}
                    canEdit={canEdit}
                    mapShow={mapShow}
                />
            </Container>

            {!!videoUrl && (desktop || mapShow) && (
                <Container>
                    <div className={styles.playerContainer}>
                        {videoLoading ? <Loading className={styles.loading} /> : null}
                        <VideoPlayer
                            className={styles.player}
                            url={videoUrl}
                            onReady={() => {
                                setVideoLoading(false);
                            }}
                        />
                    </div>
                </Container>
            )}

            {!mapShow && <div ref={firstRef} style={{ height: '200px', width: '100%' }} />}

            {(filteredSchedules.length && (
                <Container>
                    <TitleContent
                        title={t('course.details.content.schedule')}
                        content={
                            !mapShow ? (
                                <Skeleton active loading={!mapShow} round paragraph={{ rows: 2 }} />
                            ) : (
                                <Schedule
                                    schedules={filteredSchedules}
                                    selectedSchedule={selectedSchedule}
                                    setSelectedSchedule={setSelectedSchedule}
                                />
                            )
                        }
                    />
                </Container>
            )) ||
                null}
            <Modal
                visible={register && user}
                footer={null}
                onCancel={() => handleRegisterClose()}
                width={600}
                closeIcon={<img src="/static/images/course/closeModal.svg" alt="close" />}
            >
                {register && (
                    <Register
                        course={course}
                        orderCourse={orderCourse}
                        orderLoading={orderLoading}
                        selectedSchedule={selectedSchedule}
                        setJustRequested={setJustRequested}
                        justRequested={justRequested}
                        isRegistered={isRegistered}
                        useSchoolPayment={!!payment}
                    />
                )}
            </Modal>

            {mapShow && (
                <Container>
                    {mapShow && (
                        <div className={styles.schoolInfo}>
                            <SchoolInfo
                                school={school}
                                schoolGalleryHidden={courseRaw.schoolGalleryHidden}
                            />
                        </div>
                    )}
                </Container>
            )}

            {!!blog && !!blog.shortId && mapShow && !payment && (
                <Container>
                    <TitleContent
                        title={
                            mapShow && (
                                <CardListHeader title={t('school.blog.title')} link={false} />
                            )
                        }
                        content={<Blog blog={blog} />}
                    />
                </Container>
            )}
            <div>
                {!!course &&
                    !!location &&
                    !!Object.keys(location).length &&
                    !!location.lat &&
                    !!location.lng &&
                    !!school &&
                    !!Object.keys(school).length && (
                        <Container>
                            <TitleContent
                                title={t('course.details.content.map')}
                                content={
                                    !mapShow ? (
                                        <Skeleton.Button
                                            className={styles.mapSkeleton}
                                            active
                                            loading={!mapShow}
                                            round
                                            paragraph={{ rows: 2 }}
                                            style={{
                                                height: !smallScreen ? '500px' : '300px',
                                                width: '100%',
                                            }}
                                        />
                                    ) : (
                                        <Map
                                            location={location}
                                            school={school}
                                            show={courseRaw.showAddress}
                                            Button
                                            isStaticMap
                                        />
                                    )
                                }
                            />
                        </Container>
                    )}
            </div>
            {!payment && (
                <Container className={styles.registerList}>
                    <RegisterList
                        course={course}
                        favouriteUrl={favouriteUrl}
                        handleFavouriteClick={handleFavouriteClick}
                        locale={locale}
                        school={school}
                        canRegister={canRegister}
                        selectedSchedule={selectedSchedule}
                        setSelectedSchedule={setSelectedSchedule}
                        schedules={schedules}
                        setRegister={setRegister}
                        join={join}
                        lastRequestId={lastRequestId}
                    />
                </Container>
            )}
            {!payment && (
                <div ref={containerRef}>
                    <Container>
                        <TitleContent
                            // title={t('details.reviews.title')}
                            content={
                                <div className={styles.revewsContainer}>
                                    {(user &&
                                        reviews &&
                                        !reviews.some(
                                            review => review.user && review.user.id === user.id,
                                        )) ||
                                    !user ? (
                                        <ReviewForm
                                            externalId={courseRaw.shortId}
                                            pushReview={pushReview}
                                            id={courseRaw.id}
                                            type="course"
                                        />
                                    ) : null}
                                    {reviews && <Reviews loading={loading} reviews={reviews} />}
                                </div>
                            }
                        />
                    </Container>
                </div>
            )}
            {!payment && (
                <Container>
                    <SubscribeCourses user={user} course={course} />
                </Container>
            )}
            {!payment && (
                <Container>
                    <ModalCoursesSubscribe modalShow={modalShow} user={user} course={course} />
                </Container>
            )}
            {!mapShow && !payment && (
                <div ref={secondRef} style={{ height: '200px', width: '100%' }} />
            )}

            {!!relatedCourses && !!relatedCourses.length && !payment && (
                <Container>
                    <TitleContent
                        title={
                            <CardListHeader
                                title={t('course.courses.related')}
                                linkOptions={{
                                    route: 'search',
                                    queryParams: {
                                        category:
                                            courseRaw && courseRaw.category
                                                ? courseRaw.category.label
                                                : null,
                                        language:
                                            courseRaw && courseRaw.language
                                                ? courseRaw.language.Name
                                                : null,
                                    },
                                }}
                            />
                        }
                        content={
                            <CardList
                                data={relatedCourses}
                                type="course"
                                noButton
                                reference="similar"
                            />
                        }
                    />
                </Container>
            )}

            {!!otherCourses && !!otherCourses.length && mapShow && !payment && (
                <Container>
                    <TitleContent
                        title={
                            mapShow && (
                                <CardListHeader
                                    title={t('course.details.content.course_suggestions')}
                                    linkOptions={{
                                        route: 'search',
                                        queryParams: {
                                            school: school.shortId,
                                        },
                                    }}
                                />
                            )
                        }
                        content={
                            <CardList
                                data={otherCourses}
                                type="course"
                                noButton
                                reference="sameSchool"
                            />
                        }
                    />
                </Container>
            )}

            {!!(showJobs && jobsData && jobsData.total) && !payment && (
                <Container>
                    <section>
                        <TitleContent
                            title={<CardListHeader title={t('jobs.path')} />}
                            content={
                                <CardListJob data={jobsData.jobs} type="course" reference="path" />
                            }
                        />
                    </section>
                </Container>
            )}

            {!modalShow && <div ref={thirdRef} style={{ height: '200px', width: '100%' }} />}

            {!!paths && !!paths.length && mapShow && (
                <Container>
                    <h2>{t('courses.professions')}</h2>
                    <div className={styles.professions}>
                        {paths.map(p => (
                            <Professions
                                key={p.id}
                                id={p.id}
                                image={p.image}
                                title={p[`title_${locale}`]}
                                profession={p.profession[`title_${locale}`]}
                                description={p[`description${locale}`] || p.description}
                            />
                        ))}
                    </div>
                </Container>
            )}

            {!!course && !!course.price && <JsonLD data={course} />}
        </div>
    );
};

Course.propTypes = {
    load: PropTypes.bool,
    datas: PropTypes.object,
    router: PropTypes.object,
};

Course.defaultProps = {
    datas: {},
    load: false,
    router: {},
};

Course.getInitialProps = async (
    { req, res, query },
    router,
    { locationProps: { locations }, settingsProps: { domainSettings, domainsSettings } },
) => {
    if (process.browser) {
        return {
            load: true,
        };
    }

    const datas = query.id
        ? await getCourseByShortId(query.id, {
              countries: locations
                  .filter(location => location.default)
                  .map(location => location.country),
          })
        : null;

    if (!datas || !datas.course) {
        res.statusCode = 404;
    }

    const { domainCountries, defaultDomain, domain } = domainSettings;

    if (req && datas.course) {
        const {
            link,
            shortId,
            school: { link: schoolLink },
            location: { country },
        } = datas.course;

        let redirectDomain;

        if (domainCountries[domain] && !domainCountries[domain].includes(country)) {
            const domainsArray = Object.keys(domainCountries);
            for (let i = 0; i < domainsArray.length; i++) {
                if (domainCountries[domainsArray[i]].includes(country)) {
                    redirectDomain = domainsArray[i];
                    break;
                }
                if (!domain.includes('course.de')) {
                    redirectDomain = defaultDomain;
                }
            }
        }

        if (redirectDomain) {
            const { defaultLocale } = domainsSettings.find(d => d.domain === redirectDomain);

            res.writeHead(301, {
                Location: `https://${redirectDomain}/${defaultLocale}/courses/${schoolLink}/${link}/${shortId}`,
            });
            res.end();
        }
    }

    return {
        datas,
        load: false,
    };
};

export default withRouter(Course);
